/* eslint-disable no-confusing-arrow */
import React, { useState, useCallback, useEffect, useMemo, useContext } from 'react';
import { SearchState } from '@customTypes/search';
import HotelApi, { HotSpotHotelApi, HotelApiAlgolia } from '@customTypes/hotel-api';
import User from '@customTypes/user';
import { Location } from '@components/Hotels/types';
import { GetServerSideProps } from 'next';
import { ParseSearchUrl, GenerateSearchUrl } from '@helpers/url';
import { useRouter } from 'next/router';
import SearchStateProvider, {
  GetDefaultSearchState,
  useDefaultContextImplementation,
} from '@context/SearchContext2025';
import { DrawerProvider } from '@context/DrawerContext';
import { GetHotelsFromApi, HotelResponse, NoopResponse } from '@utils/Search/search';
import ResortpassAnimation from '@components/common/ResortPassAnimation/ResortPassAnimation';
import SRPFilters from '@components/SearchExperiment/SRPFilters/SRPFilters';
import Head from 'next/head';
import HotelCard from '@components/SearchExperiment/HotelCard';
import { srpHeading } from '@constants/headingText';
import SimpleLayout from '@components/Layout/SimpleLayout';
import InfiniteScroll from 'react-infinite-scroll-component';
import useFavorites from '@hooks/useFavorites';
import useSessionStorage from '@hooks/useSessionStorage';
import sessionStorageKeys from '@constants/sessionStorageKeys';
import dateWithinLastNDays from '@helpers/dateWithinLastNDays';
import {
  useAmplitudeViewSRPTracker,
  useAmplitudeApplyFilterTracker,
  useAmplitudeSelectCardTracker,
  useAmplitudeViewHotelCardsTracker,
  useAmplitudeNoResultsTracker,
  useAmplitudeSearchTracker,
} from '@hooks/useAmplitudeTrackers';
import { getCurrencyData, trackAlgoliaEvent } from '@utils/services';
import { AlgoliaInsightsPayload } from '@customTypes/algolia';
import { search as GTMSearch } from '@events/globals';
import {
  srpAlternateSearchResultClicked,
  srpBadgeHotelCardClicked,
  srpHighlightedSearchResultClicked,
  SRPHotelCardClick,
} from '@events/SRP';
import { useCookies } from 'react-cookie';
import useCurrentUser from '@hooks/useCurrentUser';
import { unionizeFavorites } from '@helpers/addHotelToFavorites';
import { hotelFavoriteTappedOnSRP } from '@events/myFavorites';
import { getServerSideUserLocation } from '@context/GeoIpDetectionContext';
import Header from '@components/SearchExperiment/Header/Header';
import useCookieConsent from '@hooks/useCookieConsent';
import { store } from '@context/store';
import { CurrencyData } from '@customTypes/currency';
import actionTypes from '@context/actionTypes';
import useIsMobile from '@hooks/useIsMobile';
import Button from '@solar/common/Button/Button';
import { getDateWithSlashes } from '@helpers/date';
import { getExperimentValue } from '@utils/amplitudeSSR';
import { CATEGORY_SEARCH_SRP_VIEWED } from '@constants/amplitudeEvents';
import { useEvents } from '@events/EventsProvider';
import SRPMarketingModule from '@components/MarketingModule/SRPMarketingModule';
import { ReferralCampaignFeatureFlagType } from '@context/FeatureFlagContext';
import { GetCanonicalURL } from '@helpers/environment';
import { format } from 'date-fns';
import { ExperimentType } from '@customTypes/context';

/**
 * getPageTitle returns the title of the page.
 * @param hotelOrLocationName - The name of the hotel or location.
 */
const getPageTitle = (hotelOrLocationName: string, productCategory: string = ''): string => {
  switch (productCategory) {
    case 'pool':
      return `Best Hotel Pool Passes and Cabanas in ${hotelOrLocationName} | ResortPass`;
    case 'spa':
      return `Best Spa Passes and Treatments in ${hotelOrLocationName} | ResortPass`;
    case 'day room':
      return `Best Day Use Rooms in ${hotelOrLocationName} | ResortPass`;
    default:
      return `Best Pool & Spa Hotel Day Passes in ${hotelOrLocationName} | ResortPass`;
  }
};

const getPageDescription = (
  hotelOrLocationName: string,
  productCategory: string = '',
  hotels: HotelApiAlgolia[] = [],
): string => {
  const isOverTenHotelsAvailable = hotels.length > 10;
  const quantifier = isOverTenHotelsAvailable ? `over ${hotels.length} of the best` : 'the best';
  switch (productCategory) {
    case 'pool':
      return (
        `Book a pool pass, cabana, or daybed at ${quantifier} hotels in ${hotelOrLocationName}. ` +
        'Enjoy the pool and amenities for the day at a luxury hotel near you!'
      );
    case 'spa':
      return (
        `Book a spa or treatment at the best hotels and spas in ${hotelOrLocationName}. ` +
        'Enjoy the spa and amenities for the day at a luxury hotel near you!'
      );
    case 'day room':
      return (
        `Book a room for the day at the best hotels in ${hotelOrLocationName}. ` +
        'Enjoy the hotel and amenities for the day at a luxury hotel near you!'
      );
    default:
      return (
        `Book a day pass at ${quantifier} hotels in ${hotelOrLocationName}. ` +
        'Enjoy the pool, spa, and amenities for the day at a luxury hotel near you!'
      );
  }
};

/**
 * Generates the canonical URL for the search page by removing the city_alias_id
 * from the search state
 */
export const getCanonicalUrl = (searchState: SearchState): string => {
  const canonicalSearchState = {
    ...searchState,
    filters: {
      ...searchState.filters,
      city_alias_id: 0,
    },
  };
  return GetCanonicalURL(GenerateSearchUrl(canonicalSearchState));
};

/**
 * GetHighlightedHotel is a function that fetches the selected hotel
 * @param searchState - The search state
 * @returns the selected hotel or null
 */
async function GetHighlightedHotel(searchState: SearchState): Promise<HotelApiAlgolia | null> {
  const { hotel_name: hotelName, date, product_category: productCategory } = searchState;
  if (hotelName) {
    const ss = GetDefaultSearchState();
    ss.hotel_name = hotelName;
    ss.date = date;
    const hotelsFromAPI = await GetHotelsFromApi(ss, undefined, productCategory);
    return hotelsFromAPI?.hotels.length ? hotelsFromAPI?.hotels[0] : null;
  }
  return null;
}

/**
 * GetHotels is a function that fetches the hotels
 * @param searchState - The search state
 * @returns the hotels
 */
const GetHotels = async (searchState: SearchState) =>
  GetHotelsFromApi({
    ...searchState,
    hotel_name: '',
  });

const GetLocationName = (searchState: SearchState, hotels?: HotelApiAlgolia[] | null) => {
  const { city, state_code: stateCode } = searchState;

  const hotel = (() => {
    if (!hotels) return null;
    if (hotels.length === 1) return hotels[0];
    if (city) {
      const hotelInCity = hotels.find(
        (h) => h.cityName.trim().toLowerCase() === city.trim().toLowerCase(),
      );
      if (hotelInCity) return hotelInCity;
    }
    return hotels[0];
  })();

  const defaultValue = (() => {
    if (hotel) return [hotel.cityName, hotel.state].join(', ');
    if (city) {
      city
        .split(' ')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    return '';
  })();

  if (city) return defaultValue;
  if (stateCode) return hotel?.state || defaultValue || stateCode.toUpperCase();
  return defaultValue;
};

/**
 * CallGTMSearchTracker is a function that sends search events to GTM
 * @param searchState - The search state
 * @param data - The hotel data
 */
const CallGTMSearchTracker = (searchState: SearchState, data: HotelApiAlgolia[] = []) => {
  const { search_term: term, date } = searchState;
  const parseDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map((part) => parseInt(part, 10));
    return new Date(y, m - 1, d);
  };
  const dateParsed = date && date !== 'any' ? parseDate(date) : undefined;
  GTMSearch(term, data.length ? data[data.length - 1].queryID : '', dateParsed);
};

const GetAlgoliaSearchPayload = (
  h: HotelApiAlgolia,
  position: number,
  user: User | null = null,
) => {
  const payload: AlgoliaInsightsPayload = {
    event_type: 'click',
    index_name: h.indexName,
    query_id: h.queryID,
    object_id: h.objectID,
    position: position.toString(),
    user_token: user ? user.id.toString() : undefined,
  };
  return payload;
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  try {
    // Get the user's location
    const userLocation = await getServerSideUserLocation(context);

    // Generate the search state from the URL
    const searchState = ParseSearchUrl(context.req.url ?? '', userLocation, true);

    // -----------------------------------------------------------------
    // Algolia Optimization --------------------------------------------
    // -----------------------------------------------------------------
    // If the user is in the same city as the search, we can optimize
    // by providing the alias id to the search state.
    // Our backend uses a faster index for querying when the alias id
    // is present.
    // -----------------------------------------------------------------
    if (!searchState.filters.city_alias_id && searchState.city && userLocation) {
      if (
        userLocation.type === 'alias' &&
        searchState.city.toLowerCase() === userLocation.city_name.toLowerCase() &&
        searchState.state_code.toLowerCase() === userLocation.state_code.toLowerCase()
      ) {
        searchState.filters.city_alias_id = userLocation.id;
      }
    }

    // -----------------------------------------------------------------
    // Parallel fetches of initial search results ----------------------
    // -----------------------------------------------------------------
    const [
      highlightedHotel,
      hotels,
      referralCampaign,
      hotelOffersExperiment,
      defaultSearchPoolExperiment,
    ] = await Promise.all([
      GetHighlightedHotel(searchState),
      GetHotels(searchState),
      getExperimentValue<ReferralCampaignFeatureFlagType>(context, 'referral-campaign').catch(
        () => null,
      ),
      getExperimentValue<'on' | null>(context, 'hotel-offers').catch(() => null),
      getExperimentValue<ExperimentType>(context, 'default-search-pool'),
    ]);

    return {
      props: {
        defaultSearchState: searchState,
        highlightedHotel,
        defaultHotels: highlightedHotel
          ? hotels.hotels.filter((h) => h.id !== highlightedHotel.id)
          : hotels.hotels,
        defaultHotSpotHotels: hotels?.hotSpotHotels ?? [],
        moreHotelsPaginationParams: hotels.pagination ?? null,
        referralCampaign,
        userLocation,
        hotelOffersExperiment,
        defaultSearchPoolExperiment,
      },
    };
  } catch (error) {
    const searchState = ParseSearchUrl(context.req.url ?? '');
    return {
      props: {
        userLocation: null,
        defaultSearchState: searchState,
        highlightedHotel: null,
        defaultHotels: [],
        defaultHotSpotHotels: [],
        referralCampaign: null,
        moreHotelsPaginationParams: null,
        hotelOffersExperiment: null,
        defaultSearchPoolExperiment: null,
      },
    };
  }
};

type Props = {
  userLocation: Location | null;
  defaultSearchState: SearchState;
  highlightedHotel: HotelApiAlgolia | null;
  defaultHotels: HotelApiAlgolia[];
  defaultHotSpotHotels: HotSpotHotelApi[];
  moreHotelsPaginationParams: {
    stage: number;
    page: number;
    limit: number;
    exclude: number[];
  } | null;
};

export default function SearchPage({
  defaultSearchState,
  highlightedHotel: defaultHighlightedHotel,
  defaultHotels,
  defaultHotSpotHotels,
  moreHotelsPaginationParams,
  userLocation,
}: Props) {
  const router = useRouter();
  const user = useCurrentUser().valueOr(null);
  const { handleCookieConsentUpdate } = useCookieConsent();
  const { getItem, setItem } = useSessionStorage();
  const { track } = useEvents();
  const globalState = useContext(store);
  const { dispatch } = globalState;
  const [currencyData, setCurrencyData] = useState<CurrencyData>();
  const source = 'srp';
  const [currentSearchState, setCurrentSearchState] = useState<SearchState>(defaultSearchState);

  // Cookie Consent
  //
  // This UseEffect is used to update the cookie consent
  useEffect(() => {
    if (userLocation) {
      const userFromUSA = userLocation.country_code.toLowerCase() === 'us';
      handleCookieConsentUpdate(userFromUSA);
      setItem(sessionStorageKeys.USER_FROM_USA, userFromUSA.toString());
    }
  }, [handleCookieConsentUpdate, userLocation, setItem]);

  // -------------------------------------------------------------------
  // Search ------------------------------------------------------------
  // -------------------------------------------------------------------
  // SearchResponse contains all of the parameters for fetching
  // the next set of hotels from the API.
  // -------------------------------------------------------------------
  const [searchResponse, setSearchResponse] = useState<HotelResponse>({
    hotels: defaultHotels,
    hotSpotHotels: defaultHotSpotHotels,
    moreHotels: !!moreHotelsPaginationParams,
    next: async () =>
      moreHotelsPaginationParams
        ? GetHotelsFromApi(defaultSearchState, moreHotelsPaginationParams)
        : NoopResponse,
    ...(moreHotelsPaginationParams ? { pagination: moreHotelsPaginationParams } : {}),
  });

  // -------------------------------------------------------------------
  // isLoading: used to show the ResortPass Loader----------------------
  // -------------------------------------------------------------------
  const [isLoading, setIsLoading] = useState(false);

  // -------------------------------------------------------------------
  // Hotels/Deduped Hotels: Used to show the hotel cards ----------------
  // -------------------------------------------------------------------
  const [hotels, setHotels] = useState<HotelApiAlgolia[]>(defaultHotels);
  const [highlightedHotel, setHighlightedHotel] = useState<HotelApiAlgolia | null>(
    defaultHighlightedHotel,
  );

  const [hotSpotHotels, setHotSpotHotels] = useState<HotSpotHotelApi[]>(defaultHotSpotHotels);
  const dedupedHotels = useMemo(() => {
    const set = new Set<number>();
    if (highlightedHotel) set.add(highlightedHotel.id);
    return hotels.filter((h) => {
      if (set.has(h.id)) return false;
      set.add(h.id);
      return true;
    });
  }, [hotels, highlightedHotel]);

  // inCity vs Nearby hotels
  const nearByIndex = useMemo(() => dedupedHotels.findIndex((h) => h.isNearBy), [dedupedHotels]);

  const inCityHotels = useMemo(() => {
    if (!dedupedHotels.length) return [];
    if (nearByIndex === -1) return dedupedHotels;
    return dedupedHotels.slice(0, nearByIndex);
  }, [dedupedHotels, nearByIndex]);

  const nearbyCityHotels = useMemo(() => {
    if (!dedupedHotels.length || nearByIndex === -1) return [];
    return dedupedHotels.slice(nearByIndex);
  }, [dedupedHotels, nearByIndex]);

  // -------------------------------------------------------------------
  // SearchContext: Used to perform search and search state mutation ---
  // -------------------------------------------------------------------
  // LoadMore is a function that is called when the
  // user clicks the "Show More Hotels" button.
  // -------------------------------------------------------------------
  const [loadMoreClicked, setLoadMoreClicked] = useState(false);

  // Amplitude tracker for searches
  const amplitudeSearchTracker = useAmplitudeSearchTracker(source);
  const amplitudeViewHotelCardsTracker = useAmplitudeViewHotelCardsTracker(source);

  const searchFn = async (searchState: SearchState) => {
    // Reset the results
    setLoadMoreClicked(false);
    // Update the URL
    amplitudeSearchTracker(searchState);
    setCurrentSearchState(searchState);
    // Fetch hotels
    // TODO: provide and error state if we cannot fetch hotels
    const [res, selectedHotel] = await Promise.all([
      GetHotels(searchState),
      GetHighlightedHotel(searchState),
    ]);

    setHighlightedHotel(selectedHotel);
    setSearchResponse(res);
    setHotels([...res.hotels]);
    setHotSpotHotels([...res.hotSpotHotels]);
    const url = GenerateSearchUrl(searchState);
    router.push(url, undefined, { shallow: true, scroll: true });
  };

  const searchContext = useDefaultContextImplementation(
    defaultSearchState,
    // Custom Search Function
    searchFn,
  );

  // loadMore: a function that is called when the user clicks the "Show More Hotels" button.
  const loadMore = useCallback(async () => {
    setIsLoading(true);
    const res = await searchResponse.next();
    setLoadMoreClicked(true);
    setSearchResponse(res);
    setHotels((prev) => [...prev, ...res.hotels]);
    setIsLoading(false);
    amplitudeViewHotelCardsTracker(searchContext.searchParams, res.hotels);
  }, [searchResponse, setHotels, setSearchResponse, amplitudeViewHotelCardsTracker, searchContext]);

  const {
    searchParams: { product_category: productCategory },
  } = searchContext;

  // -------------------------------------------------------------------
  // ResortPass Page Loader --------------------------------------------
  // -------------------------------------------------------------------
  // Show the Page Loader when a search is in progress.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (searchContext.status === 'searchStart') {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [searchContext.status]);

  const selectedVibes = Object.keys(searchContext.searchParams.filters.vibes).filter(
    (key) =>
      searchContext.searchParams.filters.vibes[
        key as keyof typeof defaultSearchState.filters.vibes
      ],
  );

  const locationOrHotelName = useMemo(() => {
    if (highlightedHotel) return highlightedHotel.name;
    return GetLocationName(searchContext.searchParams, hotels.length ? hotels : null);
  }, [searchContext, highlightedHotel, hotels]);

  // -------------------------------------------------------------------
  // Amplitude VIEW_SRP_PAGE Tracking ----------------------------------
  // -------------------------------------------------------------------
  const ViewSRPAmplitudeTracker = useAmplitudeViewSRPTracker();

  // This UseEffect is used to track the initial search results page view.
  useEffect(() => {
    ViewSRPAmplitudeTracker(defaultSearchState, defaultHotels, defaultHotSpotHotels);
    amplitudeViewHotelCardsTracker(defaultSearchState, defaultHotels);
  }, [
    ViewSRPAmplitudeTracker,
    amplitudeViewHotelCardsTracker,
    defaultSearchState,
    defaultHotels,
    defaultHotSpotHotels,
  ]);

  // This UseEffect is used to track the subseqent
  // search results page views after conducting a search.
  useEffect(() => {
    if (searchContext.status === 'searchEnd') {
      ViewSRPAmplitudeTracker(searchContext.searchParams, hotels, hotSpotHotels);
      amplitudeViewHotelCardsTracker(searchContext.searchParams, hotels);
    }
  }, [
    hotels,
    hotSpotHotels,
    ViewSRPAmplitudeTracker,
    amplitudeViewHotelCardsTracker,
    searchContext.searchParams,
    searchContext.status,
  ]);

  // -------------------------------------------------------------------
  // Amplitude APPLY_FILTER TRACKING -----------------------------------
  // -------------------------------------------------------------------
  // OnFilterChange is a function that is called when the filters changes.
  // It automatically triggers a search.
  // -------------------------------------------------------------------
  const applyFilterTracker = useAmplitudeApplyFilterTracker(source);
  const onFilterChange = (s: SearchState) => {
    applyFilterTracker(s);
    searchContext.updateSearchState(s);
    searchContext.search(500); // debounce 500ms
  };

  // -------------------------------------------------------------------
  // GTM Tracking ------------------------------------------------------
  // -------------------------------------------------------------------

  // Initial Search
  useEffect(() => {
    CallGTMSearchTracker(defaultSearchState, defaultHotels);
  }, [defaultHotels, defaultSearchState]);

  // Subsequent Searches
  useEffect(() => {
    if (searchContext.status === 'searchEnd') {
      CallGTMSearchTracker(searchContext.searchParams, dedupedHotels);
    }
  }, [searchContext.status, dedupedHotels, searchContext.searchParams]);

  // -------------------------------------------------------------------
  // Favorites Code: Legacy Favorites code that needs to be refactored -
  // -------------------------------------------------------------------
  const userLocalStorageFavorites = getItem(sessionStorageKeys.USER_SESSION_FAVORITES);
  const [cookies, setCookie] = useCookies(['userInformation']);
  const isMobile = useIsMobile({ isSolarSystem: true });

  const favorites = unionizeFavorites(
    userLocalStorageFavorites,
    cookies.userInformation?.favorites,
  );

  const [userFavorites, setUserFavorites] = useState<any>(favorites);
  const { addHotelToFavorites } = useFavorites();
  const [showFavoriteToastMessage, setShowFavoriteToastMessage] = useState<boolean>(false);
  const [saveToFavorites, setSaveToFavorites] = useState<boolean>(false);

  const handleAddingHotelIdToFavorites = (hotel: HotelApi) => {
    addHotelToFavorites(
      cookies,
      setCookie,
      hotel,
      setShowFavoriteToastMessage,
      userFavorites,
      setSaveToFavorites,
      setItem,
      getItem,
      setUserFavorites,
    );

    if (!userFavorites.includes(hotel.id.toString())) {
      hotelFavoriteTappedOnSRP(hotel.id, hotel.name);
    }
  };

  const removeAllFilters = () => {
    searchContext.clearFilters();
    searchContext.search();
  };

  // -------------------------------------------------------------------
  // No Results Flag ---------------------------------------------------
  // -------------------------------------------------------------------
  // isNoResults is a flag that is used to show the no results page
  // -------------------------------------------------------------------
  const isNoResults = useMemo(
    () => searchContext.status === 'idle' && dedupedHotels.length === 0 && !highlightedHotel,
    [dedupedHotels, highlightedHotel, searchContext.status],
  );

  // Track No Results
  const NoResultsAmplitudeTracker = useAmplitudeNoResultsTracker(source);
  useEffect(() => {
    if (isNoResults) {
      NoResultsAmplitudeTracker(searchContext.searchParams);
    }
  }, [isNoResults, searchContext.searchParams, NoResultsAmplitudeTracker]);

  // -------------------------------------------------------------------
  // Show LoadMore Button Flag -----------------------------------------
  // -------------------------------------------------------------------
  // Flag used to show the load more button
  // (initial load or after a new search)
  // -------------------------------------------------------------------
  const showLoadMoreButton = useMemo(
    () => searchResponse.moreHotels && !loadMoreClicked && !isLoading,
    [searchResponse.moreHotels, loadMoreClicked, isLoading],
  );

  // -------------------------------------------------------------------
  // Hotel Navigation --------------------------------------------------
  // -------------------------------------------------------------------
  // getHotelURL: a function that returns the URL for a hotel DLP
  // navigateToHotelDLP: a function that navigates to the hotel DLP
  // -------------------------------------------------------------------
  const getHotelURL = useCallback(
    (hotel: HotelApiAlgolia) => {
      const params = [
        ...(searchContext.searchParams.product_category
          ? [`product_category=${searchContext.searchParams.product_category}`]
          : []),
      ];
      return `/hotels/${hotel.url}${params.length ? `?${params.join('&')}` : ''}`;
    },
    [searchContext],
  );

  const selectCardTracker = useAmplitudeSelectCardTracker(source);
  const navigateToHotelDLP = useCallback(
    (hotel: HotelApiAlgolia) => {
      const index = (() => {
        if (highlightedHotel && hotel.id === highlightedHotel.id) return 1;
        return dedupedHotels.findIndex((h) => h.id === hotel.id) + (highlightedHotel ? 2 : 1);
      })();
      // ---------------------------------------------------------------
      // Old Search Results Date Behavior
      // ---------------------------------------------------------------
      // Old SRP uses the "useSmartCalendar" hook, then calls the
      // setDate exposed by the hook. The hook dispatches the "SEARCH_DATE"
      // as well as sets a key in session storage.
      //
      // Not exactly sure why we need to set the date in session storage
      // and why the two are coupled, but because there is a slew of
      // side-effects in the mini-cart for the dlp that looks at all of
      // these attributes, we need to replicate the behavior.
      // ---------------------------------------------------------------
      // See "hooks/useSmartCalendar".
      // ---------------------------------------------------------------
      const searchDate = searchContext.getDate();
      if (searchDate instanceof Date) {
        dispatch({
          type: actionTypes.SET_SEARCH_DATE,
          payload: searchDate,
        });

        setItem(sessionStorageKeys.SMART_CALENDAR_DATE, getDateWithSlashes(searchDate));
      }

      // ---------------------------------------------------------------
      // Amplitude
      // ---------------------------------------------------------------
      selectCardTracker(searchContext.searchParams, hotel, index);

      // ---------------------------------------------------------------
      // GTM
      // ---------------------------------------------------------------
      if (highlightedHotel && hotel.id === highlightedHotel.id) {
        srpHighlightedSearchResultClicked();
      } else {
        srpAlternateSearchResultClicked();
      }
      SRPHotelCardClick(hotel.name);
      if (dateWithinLastNDays(hotel.createdAt, 90)) {
        srpBadgeHotelCardClicked();
      }

      // ---------------------------------------------------------------
      // Algolia Tracking
      // ---------------------------------------------------------------
      const algoliaPayload = GetAlgoliaSearchPayload(hotel, index, user);
      trackAlgoliaEvent(algoliaPayload);
      setItem(
        sessionStorageKeys.ALGOLIA_TRACKED_HOTEL,
        JSON.stringify({ ...algoliaPayload, hotel_id: hotel.id }),
      );

      // ---------------------------------------------------------------
      // Navigate to DLP -----------------------------------------------
      // ---------------------------------------------------------------
      const fullUrlObj = new URL(getHotelURL(hotel), window.location.origin);

      // Add date parameter if searchDate is available and not "any"
      if (searchDate instanceof Date) {
        const formattedDate = format(searchDate, 'MM-dd-yyyy');
        fullUrlObj.searchParams.append('date', formattedDate);
      }

      if (!isMobile) {
        window.open(fullUrlObj.toString(), '_blank');
      } else {
        router.push(fullUrlObj.toString());
      }
    },
    [
      selectCardTracker,
      highlightedHotel,
      user,
      setItem,
      getHotelURL,
      isMobile,
      dedupedHotels,
      router,
      searchContext,
      dispatch,
    ],
  );

  // Set currency data
  useEffect(() => {
    (async () => {
      try {
        const currencyList = await getCurrencyData();
        setCurrencyData(currencyList);
        const previouslySelectedCurrency = getItem(sessionStorageKeys.SELECTED_CURRENCY);
        const defaultCurrency = currencyList.default_currency;
        const defaultCurrencyWithExchangeRate = currencyList.currencies.find(
          (item) => item.name === defaultCurrency.name,
        );
        dispatch({
          type: actionTypes.UPDATE_SELECTED_CURRENCY,
          payload: previouslySelectedCurrency
            ? JSON.parse(previouslySelectedCurrency)
            : defaultCurrencyWithExchangeRate,
        });
        dispatch({
          type: actionTypes.UPDATE_DEFAULT_CURRENCY,
          payload: defaultCurrencyWithExchangeRate,
        });
      } catch (error) {
        // No error popup. Fail silently.
      }
    })();
  }, [dispatch, getItem]);

  // Track events
  useEffect(() => {
    track(CATEGORY_SEARCH_SRP_VIEWED, {});
  }, [track]);

  return (
    <>
      <Head>
        <title>{getPageTitle(locationOrHotelName, productCategory)}</title>
        <meta
          name="description"
          content={getPageDescription(locationOrHotelName, productCategory, hotels)}
        />
        <link rel="canonical" href={getCanonicalUrl(currentSearchState)} />
      </Head>

      <SearchStateProvider value={searchContext}>
        <DrawerProvider>
          <Header currencyData={currencyData} />

          <main className="flex flex-col desktop:min-w-1312 desktop:max-w-1312 desktop:mx-auto desktop:flex-row">
            <aside className="desktop:w-310 hidden desktop:block flex-none pb-20">
              {!isMobile ? (
                <SRPFilters onChange={onFilterChange} key="desktop-srp-filters" />
              ) : null}
            </aside>

            <section className="flex flex-col flex-1 desktop:ml-8">
              {!isNoResults ? (
                <h1 className="text-solar-primary px-spacing-md py-spacing-lg desktop:pt-10 desktop:pb-8 desktop:pl-0 font-rp-basetica text-base d:text-xl leading-5 d:leading-6 -tracking-0.32 d:tracking-tight">
                  {productCategory === '' ||
                  productCategory.toLowerCase() === 'all' ||
                  !productCategory
                    ? srpHeading.all
                    : srpHeading[productCategory.toLowerCase() as keyof typeof srpHeading]}{' '}
                  {locationOrHotelName}
                </h1>
              ) : null}

              <div
                className={`${
                  !isNoResults ? '' : 'mt-6 desktop:mt-8'
                } flex flex-col gap-0 desktop:gap-8 `.trim()}
              >
                {highlightedHotel ? (
                  <div className="flex flex-col gap-0 desktop:gap-8">
                    <HotelCard
                      hotel={highlightedHotel}
                      isHighlighted
                      userFavorites={userFavorites}
                      showFavoriteToastMessage={showFavoriteToastMessage}
                      saveToFavorites={saveToFavorites}
                      handleAddingHotelIdToFavorites={handleAddingHotelIdToFavorites}
                      selectedVibes={selectedVibes}
                      productCategory={productCategory}
                      href={getHotelURL(highlightedHotel)}
                      onViewAll={() => navigateToHotelDLP(highlightedHotel)}
                      searchedDate={searchContext.searchParams.date}
                    />
                    <div className="h-0 desktop:h-1px bg-rp-button-disabled" />
                  </div>
                ) : null}

                {/* Regular Results */}
                <InfiniteScroll
                  dataLength={hotels.length}
                  next={() => !isLoading && loadMore()}
                  hasMore={searchResponse.moreHotels && loadMoreClicked}
                  loader={null}
                  className="flex flex-col gap-0 desktop:gap-8"
                >
                  {inCityHotels.map((hotel, index) => (
                    <div
                      key={hotel.id}
                      data-testid="hotel-card-container"
                      className="flex flex-col gap-8 desktop:gap-8"
                    >
                      <HotelCard
                        hotel={hotel}
                        userFavorites={userFavorites}
                        showFavoriteToastMessage={showFavoriteToastMessage}
                        saveToFavorites={saveToFavorites}
                        handleAddingHotelIdToFavorites={handleAddingHotelIdToFavorites}
                        selectedVibes={selectedVibes}
                        productCategory={productCategory}
                        href={getHotelURL(hotel)}
                        onViewAll={() => navigateToHotelDLP(hotel)}
                        searchedDate={searchContext.searchParams.date}
                      />
                      <div
                        className={`${
                          inCityHotels.length - 1 === index && nearbyCityHotels.length === 0
                            ? 'h-0'
                            : 'h-0 desktop:h-1px'
                        } bg-rp-button-disabled`}
                      />

                      {/* Show Marketing Module as the 4th result */}
                      {index === 2 ? (
                        <>
                          <SRPMarketingModule />

                          <div className="h-0 desktop:h-1px bg-rp-button-disabled" />
                        </>
                      ) : null}
                    </div>
                  ))}

                  {nearbyCityHotels.length > 0 ? (
                    <div className="font-rp-basetica px-16px py-24px mb-16px desktop:p-0 desktop:mb-0 desktop:text-20">
                      Hotels nearby
                    </div>
                  ) : null}

                  {nearbyCityHotels.map((hotel, index) => (
                    <div
                      key={hotel.id}
                      data-testid="hotel-card-container"
                      className="flex flex-col gap-8 desktop:gap-8"
                    >
                      <HotelCard
                        hotel={hotel}
                        userFavorites={userFavorites}
                        showFavoriteToastMessage={showFavoriteToastMessage}
                        saveToFavorites={saveToFavorites}
                        handleAddingHotelIdToFavorites={handleAddingHotelIdToFavorites}
                        selectedVibes={selectedVibes}
                        productCategory={productCategory}
                        href={getHotelURL(hotel)}
                        onViewAll={() => navigateToHotelDLP(hotel)}
                        searchedDate={searchContext.searchParams.date}
                      />
                      <div
                        className={`${
                          hotels.length - 1 === index ? 'h-0' : 'h-0 desktop:h-1px'
                        } bg-rp-button-disabled`}
                      />
                    </div>
                  ))}
                </InfiniteScroll>
              </div>

              {isNoResults ? (
                <div className="w-full mx-auto flex flex-col items-center px-6 desktop:w-1/2 desktop:mt-10 desktop:px-0 mb-6">
                  <p className="font-rp-basetica text-xl leading-6 mx-auto text-rp-neutral pt-10">
                    No results found
                  </p>

                  <p className="font-rp-basetica text-sm mx-auto text-rp-text-secondary text-center mt-2">
                    To find the perfect daycation, try adjusting your search or try resetting your
                    filters.
                  </p>
                  <button
                    type="button"
                    onClick={removeAllFilters}
                    id="reset-filters-button"
                    className="text-rp-shadow bg-transparent border-rp-shadow text-base border-solid border rounded py-3 px-7 w-full font-rp-basetica leading-4 cursor-pointer tracking-0.3 mt-4 mx-auto"
                  >
                    Reset Filters
                  </button>
                </div>
              ) : null}

              {showLoadMoreButton && (
                <div className="flex justify-center mx-4 my-8">
                  <Button
                    color="primarySRP"
                    size="lg"
                    type="button"
                    onClick={loadMore}
                    fullWidth
                    classes="text-base leading-4 desktop:max-w-240px"
                  >
                    Show All Hotels
                  </Button>
                </div>
              )}
            </section>
          </main>

          {isLoading ? <ResortpassAnimation /> : null}
        </DrawerProvider>
      </SearchStateProvider>
    </>
  );
}

SearchPage.getLayout = (page: React.ReactElement) => (
  <SimpleLayout userLocation={page.props.userLocation}>{page}</SimpleLayout>
);
